import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCalendarAlt, FaDownload, FaSort, FaTrash } from "react-icons/fa";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const REVENUE_EXCLUDED_STATUSES = ["Cancelled", "Return Requested"];

function TotalRevenue() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/orders/admin/orders`);
      setOrders(res.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Fetch Error:", err);
      setLoading(false);
    }
  };

  // 🗑️ DELETE ORDER FUNCTION
  const deleteOrder = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This order will be removed from revenue records!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/orders/delete/${id}`);
        if (response.status >= 200 && response.status < 300) {
          setOrders((prev) => prev.filter((o) => o._id !== id));
          Swal.fire({ icon: "success", title: "Deleted!", timer: 1000, showConfirmButton: false });
        }
      } catch (error) {
        Swal.fire("Error", "Failed to delete order", "error");
      }
    }
  };

  // --- SORTING LOGIC ---
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredOrders = orders
    .filter((order) => !REVENUE_EXCLUDED_STATUSES.includes(order.status))
    .filter((order) => {
      if (!selectedDate) return true;
      const orderDate = new Date(order.createdAt).toISOString().split("T")[0];
      return orderDate === selectedDate;
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      let valA = a[sortConfig.key] || "";
      let valB = b[sortConfig.key] || "";
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

  const totalRevenue = filteredOrders.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);

  const exportToExcel = () => {
    const reportDate = new Date().toLocaleString("en-IN");
    const dateRange = selectedDate
      ? new Date(selectedDate).toLocaleDateString("en-IN")
      : "All Dates";

    const rows = [
      ["Uniform NX - Revenue Report"],
      [`Generated On: ${reportDate}`],
      [`Date Filter: ${dateRange}`],
      [`Total Revenue: Rs. ${totalRevenue.toLocaleString("en-IN")}`],
      [],
      [
        "Sr. No.",
        "Order ID",
        "Order Date",
        "Parent Name",
        "School Name",
        "Payment Method",
        "Payment Status",
        "Order Status",
        "Items",
        "Amount"
      ],
      ...filteredOrders.map((order, index) => [
        index + 1,
        `#${order._id.slice(-6).toUpperCase()}`,
        new Date(order.createdAt).toLocaleDateString("en-IN"),
        order.parentName || "N/A",
        order.schoolName || "N/A",
        order.paymentMethod || "N/A",
        order.paymentStatus || "N/A",
        order.status || "N/A",
        order.items?.length || 0,
        Number(order.totalAmount) || 0
      ]),
      [],
      ["", "", "", "", "", "", "", "", "Grand Total", totalRevenue]
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 9 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 9 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 9 } }
    ];
    ws["!cols"] = [
      { wch: 8 },
      { wch: 14 },
      { wch: 14 },
      { wch: 24 },
      { wch: 28 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 10 },
      { wch: 14 }
    ];
    ws["!autofilter"] = {
      ref: `A6:J${Math.max(filteredOrders.length + 6, 6)}`
    };

    const amountCells = ["J4", ...filteredOrders.map((_, index) => `J${index + 7}`), `J${filteredOrders.length + 9}`];
    amountCells.forEach((cell) => {
      if (ws[cell]) ws[cell].z = '"Rs." #,##0';
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Revenue_Report");
    XLSX.writeFile(wb, `Revenue_${selectedDate || 'Overall'}.xlsx`);
  };

  if (loading) return <div className="p-5 text-center fw-bold text-success">Loading Revenue...</div>;

  return (
    <div className="p-4" style={{ background: "#f8fafc", minHeight: "100vh" }}>
      
      <button className="btn btn-md d-flex align-items-center gap-2 fw-bold text-muted border-0 bg-transparent mb-2" onClick={() => navigate("/dashboard")}>
        <FaArrowLeft /> Back
      </button>

      {/* HEADER CARD */}
      <div className="bg-white p-4 shadow-sm rounded-4 border-start border-success border-5 mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h3 className="fw-bold m-0">Revenue Management</h3>
          <p className="text-muted m-0 mt-1 small">Dynamic Cumulative Revenue</p>
        </div>
        <div className="text-end">
          <h2 className="text-success fw-bold m-0">₹{totalRevenue.toLocaleString()}</h2>
          <button className="btn btn-success btn-sm mt-2 d-flex align-items-center gap-2" onClick={exportToExcel}>
            <FaDownload /> Download Excel
          </button>
        </div>
      </div>

      {/* DATE FILTER */}
      <div className="mb-4 d-flex justify-content-end align-items-center gap-3">
        <div className="position-relative" style={{ width: "220px" }}>
          <FaCalendarAlt className="position-absolute top-50 translate-middle-y ms-3 text-muted" />
          <input type="date" className="form-control ps-5 shadow-sm border-0" style={{ borderRadius: "10px", height: "45px" }} value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
        </div>
        {selectedDate && <button className="btn btn-link text-danger fw-bold text-decoration-none" onClick={() => setSelectedDate("")}>Reset</button>}
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-4 shadow-sm overflow-hidden border">
        <table className="table align-middle mb-0 table-hover">
          <thead>
            <tr style={{ backgroundColor: "#edf9f5" }}>
              <th className="ps-4 py-3 fw-bold text-dark border-0" onClick={() => requestSort("_id")} style={{ cursor: "pointer" }}>Order Id <FaSort size={10}/></th>
              <th className="py-3 fw-bold text-dark border-0" onClick={() => requestSort("parentName")} style={{ cursor: "pointer" }}>Parent Name <FaSort size={10}/></th>
              <th className="py-3 fw-bold text-dark border-0">School Name</th>
              <th className="py-3 fw-bold text-dark border-0 text-center" onClick={() => requestSort("createdAt")} style={{ cursor: "pointer" }}>Date <FaSort size={10}/></th>
              <th className="py-3 fw-bold text-dark border-0 text-center">Amount</th>
              <th className="py-3 fw-bold text-dark border-0 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => (
                <tr key={order._id}>
                  <td className="ps-4 text-muted small">#{order._id.slice(-6).toUpperCase()}</td>
                  <td className="text-dark fw-semibold">{order.parentName || "N/A"}</td>
                  <td className="text-muted">{order.schoolName || "N/A"}</td>
                  <td className="text-secondary text-center small">{new Date(order.createdAt).toLocaleDateString()}</td>
                  <td className="text-center fw-bold text-success">₹{(order.totalAmount || 0).toLocaleString()}</td>
                  <td className="text-center">
                    <button 
                      className="btn btn-sm text-danger border-0 bg-transparent" 
                      onClick={() => deleteOrder(order._id)}
                      title="Delete Record"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="6" className="text-center py-5 text-muted">No data available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TotalRevenue;
